import { useEffect, useMemo, useState } from "react";
import { Allocation } from "@/lib/api";
import { fmtDate, fmtMoney } from "@/lib/bill-utils";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Wallet,
  ChevronLeft,
  ChevronRight,
  Plus,
  Trash2,
  Save,
  RotateCcw,
  Printer,
  AlertTriangle,
  CheckCircle2,
  Sparkles,
  ExternalLink,
  Lightbulb,
} from "lucide-react";
import { Link } from "react-router-dom";

// ---------- Helpers ----------
const startOfWeek = (d) => {
  const dt = new Date(d);
  const day = (dt.getDay() + 6) % 7;
  dt.setDate(dt.getDate() - day);
  dt.setHours(0, 0, 0, 0);
  return dt;
};
const toIso = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const TYPE_META = {
  fixed: { label: "Fixed $", suffix: "$/week" },
  percent: { label: "% of income", suffix: "%" },
  percent_of_wages: { label: "% of wages", suffix: "%" },
  auto_bills: { label: "Auto (bills)", suffix: "auto" },
  auto_plans: { label: "Auto (court/plans)", suffix: "auto" },
  leftover: { label: "Leftover", suffix: "—" },
};

const COLORS = [
  "#0369A1",
  "#DC2626",
  "#B45309",
  "#7C3AED",
  "#059669",
  "#111827",
  "#0891B2",
  "#EA580C",
];

const AllocatePage = () => {
  const [weekStart, setWeekStart] = useState(() => toIso(startOfWeek(new Date())));
  const [buckets, setBuckets] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState([]);

  const load = async (ws) => {
    setLoading(true);
    try {
      const [b, p] = await Promise.all([
        Allocation.buckets(),
        Allocation.preview(ws),
      ]);
      setBuckets(b.buckets);
      setDraft(b.buckets.map((x) => ({ ...x })));
      setPreview(p);
    } catch {
      toast.error("Could not load allocation data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(weekStart);
  }, [weekStart]);

  const shiftWeek = (days) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + days);
    setWeekStart(toIso(startOfWeek(d)));
  };

  const addBucket = () => {
    setDraft((cur) => [
      ...cur,
      {
        id: null,
        name: "New bucket",
        account_label: "",
        target_type: "fixed",
        target_value: 100,
        priority: 500 + cur.length,
        color: COLORS[cur.length % COLORS.length],
        notes: "",
        active: true,
      },
    ]);
  };

  const removeBucket = (idx) => {
    setDraft((cur) => cur.filter((_, i) => i !== idx));
  };

  const updateBucket = (idx, patch) => {
    setDraft((cur) => cur.map((b, i) => (i === idx ? { ...b, ...patch } : b)));
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      const clean = draft
        .filter((b) => (b.name || "").trim().length > 0)
        .map((b) => ({
          id: b.id || null,
          name: b.name.trim(),
          account_label: (b.account_label || "").trim(),
          target_type: b.target_type,
          target_value: Number(b.target_value) || 0,
          priority: Number(b.priority) || 500,
          color: b.color || "#111827",
          notes: b.notes || "",
          active: b.active !== false,
        }));
      await Allocation.saveBuckets(clean);
      toast.success("Buckets saved");
      setEditing(false);
      await load(weekStart);
    } catch {
      toast.error("Could not save buckets");
    } finally {
      setSaving(false);
    }
  };

  const resetDraft = () => setDraft(buckets.map((b) => ({ ...b })));

  const resetToDefaults = async () => {
    if (
      !window.confirm(
        "Reset all buckets back to Billy's recommended defaults (Wages → Super → Court/Plan → Rent → Bills → BAS → Tax → Buffer)? Your current customisations will be lost.",
      )
    )
      return;
    setSaving(true);
    try {
      const { Allocation: A } = await import("@/lib/api");
      await A.reset();
      toast.success("Buckets reset to defaults");
      setEditing(false);
      await load(weekStart);
    } catch {
      toast.error("Could not reset buckets");
    } finally {
      setSaving(false);
    }
  };

  const weekLabel = useMemo(() => {
    if (!preview) return "";
    return `${fmtDate(preview.week_start)} — ${fmtDate(preview.week_end)}`;
  }, [preview]);

  if (loading && !preview) {
    return (
      <div className="text-sm text-neutral-500">Loading allocator…</div>
    );
  }
  if (!preview) return null;

  return (
    <div data-testid="allocate-page" className="fade-rise">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="swiss-label mb-3 flex items-center gap-2">
            <Wallet className="h-3.5 w-3.5" /> Allocate
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-tight">
            Split every dollar with purpose.
          </h1>
          <p className="text-neutral-600 mt-2 max-w-2xl">
            Billy takes what came in this week and divides it into your
            buckets in priority order. Weeks where cash is tight? You&apos;ll see
            exactly which buckets got part-funded and which missed out.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="alloc-week-prev"
            onClick={() => shiftWeek(-7)}
            className="h-10 w-10 border-2 border-neutral-300 hover:border-black text-neutral-700 hover:text-black flex items-center justify-center"
            aria-label="Previous week"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            data-testid="alloc-week-today"
            onClick={() => setWeekStart(toIso(startOfWeek(new Date())))}
            className="border-2 border-neutral-300 hover:border-black text-neutral-700 hover:text-black px-4 py-2 text-[11px] uppercase tracking-widest font-bold"
          >
            This week
          </button>
          <button
            data-testid="alloc-week-next"
            onClick={() => shiftWeek(7)}
            className="h-10 w-10 border-2 border-neutral-300 hover:border-black text-neutral-700 hover:text-black flex items-center justify-center"
            aria-label="Next week"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          <button
            data-testid="alloc-print-btn"
            onClick={() => window.print()}
            className="border-2 border-black bg-white text-black hover:bg-black hover:text-white font-bold uppercase tracking-widest text-[11px] px-4 py-2 inline-flex items-center gap-2 print:hidden"
          >
            <Printer className="h-4 w-4" /> Print
          </button>
        </div>
      </div>

      {/* KPI tiles */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="swiss-card">
          <div className="swiss-label">Received this week</div>
          <div className="mt-2 font-display font-black text-2xl tabular-nums text-green-700">
            {fmtMoney(preview.received_total)}
          </div>
          <div className="mt-1 text-[10px] uppercase tracking-widest text-neutral-500">
            {weekLabel}
          </div>
        </div>
        <div className="swiss-card">
          <div className="swiss-label">Total to allocate</div>
          <div className="mt-2 font-display font-black text-2xl tabular-nums">
            {fmtMoney(preview.total_target)}
          </div>
          <div className="mt-1 text-[10px] uppercase tracking-widest text-neutral-500">
            {preview.buckets.length} bucket
            {preview.buckets.length === 1 ? "" : "s"}
          </div>
        </div>
        <div className="swiss-card">
          <div className="swiss-label">Allocated</div>
          <div
            className={`mt-2 font-display font-black text-2xl tabular-nums ${preview.fully_funded ? "text-green-700" : "text-amber-700"}`}
          >
            {fmtMoney(preview.total_allocated)}
          </div>
          <div className="mt-1 text-[10px] uppercase tracking-widest text-neutral-500">
            {preview.fully_funded ? "All buckets funded" : "Some part-paid"}
          </div>
        </div>
        <div className="swiss-card">
          <div className="swiss-label">
            {preview.total_shortfall > 0 ? "Shortfall" : "Surplus"}
          </div>
          <div
            className={`mt-2 font-display font-black text-2xl tabular-nums ${preview.total_shortfall > 0 ? "text-red-600" : "text-green-700"}`}
          >
            {fmtMoney(
              preview.total_shortfall > 0
                ? preview.total_shortfall
                : preview.surplus,
            )}
          </div>
          <div className="mt-1 text-[10px] uppercase tracking-widest text-neutral-500">
            {preview.total_shortfall > 0
              ? "still needed"
              : "left over"}
          </div>
        </div>
      </section>

      {/* Bucket allocation table */}
      <section className="swiss-card mt-6" data-testid="alloc-buckets-view">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <div className="swiss-label">This week&apos;s split</div>
            <div className="font-display font-extrabold text-xl mt-1">
              Priority order — high to low
            </div>
          </div>
          {!editing && (
            <button
              data-testid="alloc-edit-btn"
              onClick={() => setEditing(true)}
              className="border-2 border-black bg-black text-white font-bold uppercase tracking-widest text-[11px] px-4 py-2 hover:bg-white hover:text-black transition-colors inline-flex items-center gap-2 print:hidden"
            >
              Edit buckets
            </button>
          )}
        </div>

        {/* View mode: show allocation results */}
        {!editing && (
          <ul className="divide-y divide-neutral-100">
            {preview.buckets.map((b) => {
              const pct =
                b.target > 0
                  ? Math.max(
                      0,
                      Math.min(100, (b.allocated / b.target) * 100),
                    )
                  : b.allocated > 0
                    ? 100
                    : 0;
              const isShort = b.shortfall > 0;
              return (
                <li
                  key={b.id}
                  className="py-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-center"
                  data-testid={`alloc-row-${b.name.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}
                >
                  <div className="md:col-span-4 min-w-0">
                    <div className="flex items-center gap-2">
                      <span
                        className="inline-block h-3 w-3 rounded-full flex-shrink-0"
                        style={{ background: b.color }}
                      />
                      <span className="font-display font-bold truncate">
                        {b.name}
                      </span>
                    </div>
                    <div className="text-xs text-neutral-500 mt-1 truncate">
                      {b.account_label || "—"} ·{" "}
                      {TYPE_META[b.target_type]?.label || b.target_type}
                      {b.target_type === "fixed" &&
                        ` · ${fmtMoney(b.target_value)}`}
                      {b.target_type === "percent" &&
                        ` · ${b.target_value}%`}
                    </div>
                  </div>
                  <div className="md:col-span-5">
                    <div className="h-3 bg-neutral-100 border border-neutral-200 relative overflow-hidden">
                      <div
                        className="h-full"
                        style={{
                          width: `${pct}%`,
                          background: b.color,
                          opacity: isShort ? 0.7 : 1,
                        }}
                      />
                    </div>
                    <div className="flex items-center justify-between text-[10px] uppercase tracking-widest font-bold text-neutral-500 mt-1">
                      <span>
                        {fmtMoney(b.allocated)} of {fmtMoney(b.target)}
                      </span>
                      {isShort ? (
                        <span className="text-red-600">
                          -{fmtMoney(b.shortfall)} short
                        </span>
                      ) : b.target > 0 ? (
                        <span className="text-green-700">funded</span>
                      ) : null}
                    </div>
                  </div>
                  <div className="md:col-span-3 text-right">
                    <div
                      className={`font-display font-extrabold text-lg mono tabular-nums ${isShort ? "text-amber-700" : b.target === 0 ? "text-neutral-400" : "text-green-700"}`}
                    >
                      {fmtMoney(b.allocated)}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                      Priority {b.priority}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {/* Edit mode */}
        {editing && (
          <div data-testid="alloc-edit-form" className="space-y-3">
            <div className="text-xs text-neutral-600 bg-amber-50 border-2 border-amber-300 p-3">
              <span className="font-bold">Priority</span> — lower number = funded first (100 first, 999 last).{" "}
              <span className="font-bold">Fixed</span> = $ per week.{" "}
              <span className="font-bold">%</span> = of income received.{" "}
              <span className="font-bold">Auto (bills)</span> = sums bills due that week.{" "}
              <span className="font-bold">Leftover</span> = whatever&apos;s left after everything above.
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 border-b-2 border-black">
                    <th className="text-left py-2 font-bold">Priority</th>
                    <th className="text-left py-2 font-bold">Name</th>
                    <th className="text-left py-2 font-bold">Account label</th>
                    <th className="text-left py-2 font-bold">Type</th>
                    <th className="text-right py-2 font-bold">Value</th>
                    <th className="text-center py-2 font-bold">Colour</th>
                    <th className="text-right py-2 font-bold" />
                  </tr>
                </thead>
                <tbody>
                  {draft.map((b, idx) => (
                    <tr
                      key={idx}
                      className="border-b border-neutral-100"
                      data-testid={`alloc-edit-row-${idx}`}
                    >
                      <td className="py-2 w-20">
                        <Input
                          type="number"
                          value={b.priority}
                          onChange={(e) =>
                            updateBucket(idx, {
                              priority: e.target.value,
                            })
                          }
                          className="swiss-input mono"
                        />
                      </td>
                      <td className="py-2">
                        <Input
                          value={b.name}
                          onChange={(e) =>
                            updateBucket(idx, { name: e.target.value })
                          }
                          className="swiss-input"
                          data-testid={`alloc-edit-name-${idx}`}
                        />
                      </td>
                      <td className="py-2">
                        <Input
                          value={b.account_label || ""}
                          onChange={(e) =>
                            updateBucket(idx, {
                              account_label: e.target.value,
                            })
                          }
                          placeholder="e.g. Bank Wages Acct"
                          className="swiss-input"
                        />
                      </td>
                      <td className="py-2 w-36">
                        <select
                          data-testid={`alloc-edit-type-${idx}`}
                          value={b.target_type}
                          onChange={(e) =>
                            updateBucket(idx, {
                              target_type: e.target.value,
                            })
                          }
                          className="swiss-input h-10"
                        >
                          <option value="fixed">Fixed $</option>
                          <option value="percent">% of income</option>
                          <option value="percent_of_wages">% of wages</option>
                          <option value="auto_bills">Auto (bills)</option>
                          <option value="auto_plans">Auto (court/plans)</option>
                          <option value="leftover">Leftover</option>
                        </select>
                      </td>
                      <td className="py-2 w-24">
                        <Input
                          type="number"
                          value={b.target_value}
                          onChange={(e) =>
                            updateBucket(idx, {
                              target_value: e.target.value,
                            })
                          }
                          disabled={
                            b.target_type === "auto_bills" ||
                            b.target_type === "auto_plans" ||
                            b.target_type === "leftover"
                          }
                          className="swiss-input mono text-right"
                        />
                      </td>
                      <td className="py-2 text-center">
                        <input
                          type="color"
                          value={b.color || "#111827"}
                          onChange={(e) =>
                            updateBucket(idx, { color: e.target.value })
                          }
                          className="h-8 w-10 border border-neutral-300 cursor-pointer"
                          aria-label="Bucket colour"
                        />
                      </td>
                      <td className="py-2 text-right">
                        <button
                          onClick={() => removeBucket(idx)}
                          className="text-red-500 hover:text-red-700"
                          aria-label="Remove bucket"
                          data-testid={`alloc-remove-${idx}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex flex-wrap gap-2 pt-2">
              <button
                data-testid="alloc-add-bucket"
                onClick={addBucket}
                className="border-2 border-neutral-300 hover:border-black font-bold uppercase tracking-widest text-[11px] px-4 py-2 inline-flex items-center gap-2"
              >
                <Plus className="h-3.5 w-3.5" /> Add bucket
              </button>
              <button
                data-testid="alloc-save-btn"
                onClick={saveAll}
                disabled={saving}
                className="border-2 border-black bg-black text-white font-bold uppercase tracking-widest text-[11px] px-4 py-2 hover:bg-white hover:text-black transition-colors inline-flex items-center gap-2 disabled:opacity-50"
              >
                <Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save"}
              </button>
              <button
                data-testid="alloc-reset-btn"
                onClick={resetDraft}
                className="border-2 border-neutral-300 hover:border-black font-bold uppercase tracking-widest text-[11px] px-4 py-2 inline-flex items-center gap-2"
              >
                <RotateCcw className="h-3.5 w-3.5" /> Reset
              </button>
              <button
                data-testid="alloc-reset-defaults-btn"
                onClick={resetToDefaults}
                disabled={saving}
                className="border-2 border-amber-500 text-amber-700 hover:bg-amber-500 hover:text-white font-bold uppercase tracking-widest text-[11px] px-4 py-2 inline-flex items-center gap-2 disabled:opacity-50"
                title="Wipe & re-seed with Billy's recommended default buckets"
              >
                <Sparkles className="h-3.5 w-3.5" /> Reset to defaults
              </button>
              <button
                onClick={() => setEditing(false)}
                className="border-2 border-neutral-300 hover:border-black font-bold uppercase tracking-widest text-[11px] px-4 py-2"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Billy Insight — the "why" narrative */}
      {!editing && preview.insight && preview.insight.length > 0 && (
        <section className="swiss-card mt-6 border-l-4 border-l-indigo-500" data-testid="alloc-insight">
          <div className="flex items-center gap-2 mb-3">
            <Lightbulb className="h-5 w-5 text-indigo-600" />
            <div>
              <div className="swiss-label">Billy Insight</div>
              <div className="font-display font-extrabold text-xl leading-tight">
                Why the money went where
              </div>
            </div>
          </div>
          <div className="space-y-3">
            {preview.insight.map((p, i) => (
              <p
                key={i}
                className="text-sm text-neutral-800 leading-relaxed"
                data-testid={`insight-para-${i}`}
              >
                {p}
              </p>
            ))}
          </div>
        </section>
      )}

      {/* Chaseable income + Overdue bills side by side */}
      {!editing && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          {/* Chaseable income */}
          {preview.chaseable_total > 0 && (
            <section
              className="swiss-card border-l-4 border-l-green-600"
              data-testid="alloc-chaseable"
            >
              <div className="flex items-center justify-between gap-3 mb-3">
                <div>
                  <div className="swiss-label">Money you can still chase</div>
                  <div className="font-display font-extrabold text-xl leading-tight">
                    {fmtMoney(preview.chaseable_total)}
                    <span className="text-sm font-normal text-neutral-500 ml-2">
                      · {preview.chaseable_incomes.length} client
                      {preview.chaseable_incomes.length === 1 ? "" : "s"}
                    </span>
                  </div>
                </div>
              </div>
              <ul className="divide-y divide-neutral-100 max-h-80 overflow-y-auto">
                {preview.chaseable_incomes.slice(0, 20).map((i) => (
                  <li
                    key={i.id}
                    className="py-2 flex items-center justify-between gap-3 text-sm"
                    data-testid={`chaseable-${i.id}`}
                  >
                    <div className="min-w-0">
                      <div className="font-display font-bold truncate">
                        {i.client_name || "(no name)"}
                      </div>
                      <div className="text-xs text-neutral-500 truncate">
                        {i.category} · expected {fmtDate(i.expected_date)}
                        {i.days_late > 0 && (
                          <span className="text-red-600 font-bold ml-1">
                            · {i.days_late}d late
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right whitespace-nowrap">
                      <div className="font-display font-extrabold mono tabular-nums text-amber-700">
                        {fmtMoney(i.amount)}
                      </div>
                      <Link
                        to="/income"
                        className="text-[10px] uppercase tracking-widest text-neutral-500 hover:text-black inline-flex items-center gap-1"
                      >
                        chase <ExternalLink className="h-3 w-3" />
                      </Link>
                    </div>
                  </li>
                ))}
              </ul>
              {preview.chaseable_incomes.length > 20 && (
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-2">
                  … and {preview.chaseable_incomes.length - 20} more. Full list on
                  the Income tab.
                </div>
              )}
            </section>
          )}

          {/* Overdue bills — affordability suggestion */}
          {preview.overdue_bills_total > 0 && (
            <section
              className="swiss-card border-l-4 border-l-red-500"
              data-testid="alloc-overdue"
            >
              <div className="flex items-center justify-between gap-3 mb-3">
                <div>
                  <div className="swiss-label">Overdue bills — propose a plan</div>
                  <div className="font-display font-extrabold text-xl leading-tight">
                    {fmtMoney(preview.overdue_bills_total)}
                    <span className="text-sm font-normal text-neutral-500 ml-2">
                      · {preview.overdue_bills.length} bill
                      {preview.overdue_bills.length === 1 ? "" : "s"}
                    </span>
                  </div>
                </div>
              </div>
              <div className="bg-red-50 border-2 border-red-200 p-3 text-sm mb-3">
                <div className="font-bold text-red-900 mb-1">
                  Affordable split: {fmtMoney(preview.suggested_overdue_payment)}
                  /week across the whole set
                </div>
                <div className="text-neutral-700 leading-snug">
                  Roughly{" "}
                  <span className="font-bold mono">
                    {fmtMoney(
                      preview.suggested_overdue_payment /
                        Math.max(preview.overdue_bills.length, 1),
                    )}
                  </span>{" "}
                  per bill. Message each supplier:{" "}
                  <em>&quot;Sorry it&apos;s late — happy to pay $X/week until cleared, does that work?&quot;</em>{" "}
                  Locks in a plan and stops the chasing.
                </div>
              </div>
              <ul className="divide-y divide-neutral-100 max-h-64 overflow-y-auto">
                {preview.overdue_bills.slice(0, 20).map((b) => (
                  <li
                    key={b.id}
                    className="py-2 flex items-center justify-between gap-3 text-sm"
                    data-testid={`overdue-bill-${b.id}`}
                  >
                    <div className="min-w-0">
                      <Link
                        to={`/bills/${b.id}`}
                        className="font-display font-bold hover:underline truncate block"
                      >
                        {b.company}
                      </Link>
                      <div className="text-xs text-neutral-500 truncate">
                        #{b.invoice_number} · was due {fmtDate(b.due_date)}
                      </div>
                    </div>
                    <div className="text-right whitespace-nowrap">
                      <div className="font-display font-extrabold mono tabular-nums text-red-700">
                        {fmtMoney(b.remaining_balance)}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
              {preview.overdue_bills.length > 20 && (
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-2">
                  … and {preview.overdue_bills.length - 20} more.
                </div>
              )}
            </section>
          )}
        </div>
      )}

      {/* Court/plan payments callout */}
      {!editing && preview.court_plan_total > 0 && (
        <section
          className="swiss-card mt-6 border-l-4 border-l-red-700 bg-red-50/40"
          data-testid="alloc-court-plans"
        >
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="h-5 w-5 text-red-700" />
            <div>
              <div className="swiss-label text-red-900">Court / plan payments (urgent)</div>
              <div className="font-display font-extrabold text-xl leading-tight">
                {fmtMoney(preview.court_plan_total)}
                <span className="text-sm font-normal text-neutral-600 ml-2">
                  · {preview.court_plan_bills.length} instalment
                  {preview.court_plan_bills.length === 1 ? "" : "s"}
                </span>
              </div>
            </div>
          </div>
          <ul className="divide-y divide-neutral-100">
            {preview.court_plan_bills.map((b) => (
              <li
                key={b.id}
                className="py-2 flex items-center justify-between gap-3 text-sm"
                data-testid={`court-plan-${b.id}`}
              >
                <div className="min-w-0">
                  <Link
                    to={`/bills/${b.id}`}
                    className="font-display font-bold hover:underline truncate block"
                  >
                    {b.company} · #{b.series_index}/{b.series_total}
                  </Link>
                  <div className="text-xs text-neutral-500 truncate">
                    #{b.invoice_number} · due {fmtDate(b.due_date)}
                  </div>
                </div>
                <div className="font-display font-extrabold mono tabular-nums text-red-800 whitespace-nowrap">
                  {fmtMoney(b.remaining_balance)}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

export default AllocatePage;
